'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search, MapPin, X, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { API_BASE } from '@/lib/env';
import { DIRECTORY_TYPES, TYPE_ICONS, TYPE_LABELS, TYPE_COLORS, type DirectoryType } from '@/constants/directoryTypes';
import { resolveDirectoryCity } from '@/constants/directoryCities';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { trackDirectorySearchResultClick } from '@/lib/analytics';

interface CityOption {
  city: string;
  count: number;
  province: string;
}

interface SuggestListing {
  id: string;
  name: string;
  slug: string;
  type: string;
  city: string;
  logo_url?: string | null;
  photos?: Array<{ url: string; alt?: string }> | null;
}

type TypeCount = Record<DirectoryType, number>;

function listingThumb(l: SuggestListing) {
  return l.logo_url || l.photos?.[0]?.url || null;
}

// ============================================================================
// PANEL — contenido puro (input + 4 bloques + autocomplete). Reutilizable en
// el bottom sheet (móvil) y en el dropdown anclado al buscador (desktop).
// Se auto-abastece por fetch; las acciones se resuelven vía CustomEvent para
// no depender de callbacks del padre (así funciona igual en hero y en navbar).
// ============================================================================
interface DirectorySearchPanelProps {
  initialQuery?: string;
  onClose: () => void;
  cities?: CityOption[];
  typeCounts?: TypeCount | null;
  autoFocus?: boolean;
  /** Si false, el panel NO renderiza su barra de input (el input vive en el buscador de arriba). */
  showInput?: boolean;
  /** Query controlado desde fuera (cuando showInput=false; el input real es el del buscador). */
  query?: string;
}

export function DirectorySearchPanel({
  initialQuery = '',
  onClose,
  cities: citiesProp,
  typeCounts,
  autoFocus = false,
  showInput = true,
  query: controlledQuery,
}: DirectorySearchPanelProps) {
  const router = useRouter();
  const { items: recent, add: addRecent, clear: clearRecent } = useRecentSearches();

  const [ownQuery, setOwnQuery] = useState(initialQuery);
  // query controlado (input externo) tiene prioridad; si no, el propio.
  const query = controlledQuery !== undefined ? controlledQuery : ownQuery;
  const setQuery = setOwnQuery;
  const [cities, setCities] = useState<CityOption[]>(citiesProp || []);
  const [featured, setFeatured] = useState<SuggestListing[]>([]);
  const [suggestCities, setSuggestCities] = useState<CityOption[]>([]);
  const [suggestListings, setSuggestListings] = useState<SuggestListing[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = query.trim();
  const isSearching = trimmed.length >= 2;

  // Al montar: focus (si procede), cargar destacadas y ciudades (si no vinieron por prop).
  useEffect(() => {
    if (autoFocus) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/directory/suggest?featured=1`)
      .then((r) => (r.ok ? r.json() : { featured: [] }))
      .then((d) => setFeatured(d.featured || []))
      .catch(() => {});
    if (!citiesProp || citiesProp.length === 0) {
      fetch(`${API_BASE}/directory/cities`)
        .then((r) => (r.ok ? r.json() : { cities: [] }))
        .then((d) => setCities(d.cities || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autocomplete con debounce.
  useEffect(() => {
    if (!isSearching) {
      setSuggestCities([]);
      setSuggestListings([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      fetch(`${API_BASE}/directory/suggest?q=${encodeURIComponent(trimmed)}`)
        .then((r) => (r.ok ? r.json() : { cities: [], listings: [] }))
        .then((d) => {
          if (cancelled) return;
          setSuggestCities(d.cities || []);
          setSuggestListings(d.listings || []);
        })
        .catch(() => {})
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [trimmed, isSearching]);

  const goCity = (city: string) => {
    addRecent(city);
    // pSEO → landing propia; si no, filtro ?city= en /directorio.
    const plp = resolveDirectoryCity(city);
    if (plp) router.push(`/directorio/${plp.slug}`);
    else {
      window.dispatchEvent(new CustomEvent('directory:selectCity', { detail: city }));
    }
    onClose();
  };
  const goType = (type: string) => {
    window.dispatchEvent(new CustomEvent('directory:selectType', { detail: type }));
    onClose();
  };
  const goListing = (l: SuggestListing) => {
    addRecent(l.name);
    trackDirectorySearchResultClick(l.id, l.type);
    router.push(`/directorio/${l.slug}`);
    onClose();
  };
  const submitTerm = (term: string) => {
    const t = term.trim();
    if (!t) return;
    addRecent(t);
    window.dispatchEvent(new CustomEvent('directory:search', { detail: t }));
    onClose();
  };

  const topCities = cities.slice(0, 6);

  return (
    <>
      {/* Barra de búsqueda (solo cuando el panel es su propio buscador — móvil/sheet).
          En el dropdown desktop showInput=false: el input real es el del buscador de arriba. */}
      {showInput && (
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-gray-100 bg-white px-4 py-3 rounded-t-3xl md:rounded-t-2xl">
          <Search className="h-5 w-5 flex-shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitTerm(query);
            }}
            placeholder="Busca por ciudad o nombre..."
            className="w-full bg-transparent text-base text-gray-900 placeholder-gray-400 focus:outline-none"
            aria-label="Buscar en el directorio"
          />
          <button
            type="button"
            onClick={() => (query ? setQuery('') : onClose())}
            className="flex-shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={query ? 'Limpiar' : 'Cerrar'}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Contenido scrollable */}
      <div className="overflow-y-auto px-4 py-4 max-h-[calc(88vh-64px)] md:max-h-[70vh]">
        {isSearching ? (
          /* ===== MODO AUTOCOMPLETE ===== */
          <div className="space-y-1">
            {suggestCities.length > 0 && (
              <>
                <p className="px-1 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Ciudades</p>
                {suggestCities.map((c) => (
                  <button
                    key={`city-${c.city}`}
                    type="button"
                    onClick={() => goCity(c.city)}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-gray-50"
                  >
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                      <MapPin className="h-5 w-5 text-gray-500" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-gray-900">{c.city}</span>
                      <span className="block truncate text-sm text-gray-500">
                        {c.province ? `${c.province} · ` : ''}{c.count} {c.count === 1 ? 'resultado' : 'resultados'}
                      </span>
                    </span>
                  </button>
                ))}
              </>
            )}

            {suggestListings.length > 0 && (
              <>
                <p className="px-1 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Establecimientos</p>
                {suggestListings.map((l) => {
                  const Icon = TYPE_ICONS[(l.type as keyof typeof TYPE_ICONS)] ?? MapPin;
                  const thumb = listingThumb(l);
                  return (
                    <button
                      key={`l-${l.id}`}
                      type="button"
                      onClick={() => goListing(l)}
                      className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-gray-50"
                    >
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100">
                        {thumb ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={thumb} alt={l.name} className="h-full w-full object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
                        ) : (
                          <Icon className="h-5 w-5" style={{ color: TYPE_COLORS[l.type] }} />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-gray-900">{l.name}</span>
                        <span className="block truncate text-sm text-gray-500">
                          {TYPE_LABELS[l.type] ?? l.type}{l.city ? ` · ${l.city}` : ''}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </>
            )}

            {loading && suggestCities.length === 0 && suggestListings.length === 0 && (
              <p className="px-2 py-6 text-center text-sm text-gray-400">Buscando…</p>
            )}

            <button
              type="button"
              onClick={() => submitTerm(query)}
              className="mt-2 flex w-full items-center gap-3 rounded-lg border-t border-gray-100 px-2 py-3 text-left hover:bg-gray-50"
            >
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                <Search className="h-5 w-5 text-gray-500" />
              </span>
              <span className="flex-1 font-medium text-gray-900">Buscar &ldquo;{trimmed}&rdquo;</span>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        ) : (
          /* ===== ESTADO VACÍO ===== */
          <div className="space-y-6">
            {/* Recientes */}
            {recent.length > 0 && (
              <section>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Búsquedas recientes</h3>
                  <button type="button" onClick={clearRecent} className="text-xs text-gray-400 hover:text-gray-600">Borrar</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recent.map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => submitTerm(term)}
                      className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                    >
                      <Clock className="h-3.5 w-3.5 text-gray-400" /> {term}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Fichas destacadas (primero: da vidilla visual) */}
            {featured.length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Destacados</h3>
                <div className="space-y-1">
                  {featured.map((l) => {
                    const Icon = TYPE_ICONS[(l.type as keyof typeof TYPE_ICONS)] ?? MapPin;
                    const thumb = listingThumb(l);
                    return (
                      <button
                        key={`f-${l.id}`}
                        type="button"
                        onClick={() => goListing(l)}
                        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-gray-50"
                      >
                        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100">
                          {thumb ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={thumb} alt={l.name} className="h-full w-full object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
                          ) : (
                            <Icon className="h-5 w-5" style={{ color: TYPE_COLORS[l.type] }} />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-gray-900">{l.name}</span>
                          <span className="block truncate text-sm text-gray-500">{TYPE_LABELS[l.type] ?? l.type}{l.city ? ` · ${l.city}` : ''}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Categorías */}
            <section>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Categorías</h3>
              <div className="grid grid-cols-2 gap-2">
                {DIRECTORY_TYPES.map((t) => {
                  const Icon = TYPE_ICONS[t];
                  const count = typeCounts?.[t];
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => goType(t)}
                      className="flex items-center gap-3 rounded-xl border border-gray-200 px-3 py-3 text-left hover:border-gray-300 hover:bg-gray-50"
                    >
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${TYPE_COLORS[t]}15` }}>
                        <Icon className="h-5 w-5" style={{ color: TYPE_COLORS[t] }} />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-gray-900">{TYPE_LABELS[t]}</span>
                        {typeof count === 'number' && <span className="block text-xs text-gray-400">{count}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Ciudades destacadas */}
            {topCities.length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Ciudades destacadas</h3>
                <div className="flex flex-wrap gap-2">
                  {topCities.map((c) => {
                    const isPseo = !!resolveDirectoryCity(c.city);
                    return (
                      <button
                        key={c.city}
                        type="button"
                        onClick={() => goCity(c.city)}
                        className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                      >
                        <MapPin className="h-3.5 w-3.5 text-gray-400" />
                        <span className="font-medium">{c.city}</span>
                        <span className="text-gray-400">{c.count}</span>
                        {isPseo && <Sparkles className="h-3 w-3 text-amber-400" />}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// DROPDOWN (desktop) — panel anclado justo bajo el input del buscador, estilo
// GetYourGuide. Se renderiza DENTRO del contenedor relative del buscador.
// ============================================================================
interface DirectorySearchDropdownProps {
  open: boolean;
  onClose: () => void;
  /** query controlado: el input real es el del buscador de arriba (hero/navbar). */
  query: string;
  cities?: CityOption[];
  typeCounts?: TypeCount | null;
}

export function DirectorySearchDropdown({ open, onClose, query, cities, typeCounts }: DirectorySearchDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Cerrar al hacer click fuera o Esc. "Fuera" = fuera del buscador completo
  // (el <form> que contiene el input trigger + este dropdown), para que un click
  // en el propio input no lo cierre nada más abrirlo.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      // "Fuera" = fuera del contenedor del buscador (marcado con data-search-box),
      // que envuelve el input trigger + este dropdown. Así un click en el trigger no lo cierra.
      const box = ref.current?.closest('[data-search-box]') ?? ref.current;
      if (box && !box.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      className="absolute left-0 right-0 top-[calc(100%+8px)] z-[300] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
    >
      {/* showInput=false: el campo de escribir es el del buscador de arriba (no duplicar) */}
      <DirectorySearchPanel query={query} showInput={false} onClose={onClose} cities={cities} typeCounts={typeCounts} />
    </div>
  );
}

// ============================================================================
// SHEET (móvil) — bottom sheet que sale de abajo, estilo GetYourGuide.
// ============================================================================
interface DirectorySearchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuery?: string;
  cities?: CityOption[];
  typeCounts?: TypeCount | null;
}

export function DirectorySearchSheet({ open, onOpenChange, initialQuery, cities, typeCounts }: DirectorySearchSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideCloseButton
        className="inset-x-0 bottom-0 h-[88vh] w-full max-w-full gap-0 rounded-t-3xl border-0 p-0"
      >
        <SheetTitle className="sr-only">Buscar en el directorio</SheetTitle>
        <DirectorySearchPanel initialQuery={initialQuery} onClose={() => onOpenChange(false)} cities={cities} typeCounts={typeCounts} />
      </SheetContent>
    </Sheet>
  );
}

export default DirectorySearchSheet;
